import styled from 'styled-components/native';
import { BACK_BUTTON_MARGIN, COLORS } from '../../config/designSystem/Colors';
import { BIG_HORIZONTAL_MARGIN_FN } from '../../config/designSystem/Layout';
import { CountryCode, getPhoneCode, parsePhoneNumber } from 'libphonenumber-js';
import { FC, useEffect, useRef, useState } from 'react';
import { Feather } from '@expo/vector-icons';
import { TextInput, TouchableOpacity } from 'react-native';
import { mainTypoBold, mainTypoRegular } from '../../config/designSystem/typography';
import { useAvailableDimensions } from '../../hooks/ui/useAvailableDimensions';
import { useIsSmallWeb } from '../../hooks/ui/useIsSmallWeb';
import { useStores } from '../../hooks/useStores';

const leftMargin = (windowWidth: number, sessionMargin?: number) =>
  sessionMargin !== undefined ? sessionMargin : BIG_HORIZONTAL_MARGIN_FN(windowWidth);
const rightMargin = (sessionMargin?: number) =>
  sessionMargin !== undefined ? sessionMargin : BACK_BUTTON_MARGIN;

const Container = styled.View<{$windowWidth: number, $sessionMargin: number}>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  margin-right: ${({$sessionMargin}) => rightMargin($sessionMargin)}px;
  margin-left: ${({$windowWidth, $sessionMargin}) => leftMargin($windowWidth, $sessionMargin)}px;
`;

const InputBox = styled.View`
  flex-direction: row;
  gap: 8px;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 14px;
  padding-right: 14px;
  border-radius: 8px;
  background-color: ${COLORS.surface.contrast};
  border-color: ${COLORS.surface['inverse-primary']};
  border-width: 1px;
  align-items: center;
  max-width: 300px;
  box-shadow: 0px 0px 4px ${COLORS.brand[10]};
`;

const RegionCodeInput = styled.TextInput<{isLarge: boolean}>`
  font-family: ${mainTypoRegular};
  font-size: 16px;
  font-variant: tabular-nums;
  color: ${COLORS.content.primary};
  line-height: 20px;
  padding: 0;
  margin: 0;
  padding-left: 4px;
  padding-right: 4px;
  border-right-color: ${COLORS.border.accent};
  border-right-width: 2px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  border-left-color: ${COLORS.border.accent};
  border-left-width: 2px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  ${props => props?.isLarge ? 'width: 5ch;' : 'width: 50px;'}
`;
const PhoneInput = styled.TextInput<{isLarge: boolean}>`
  font-family: ${mainTypoRegular};
  font-size: 16px;
  font-variant: tabular-nums;
  color: ${COLORS.content.primary};
  line-height: 20px;
  width: 100%;
  padding: 0;
  margin: 0;
  ${props => props?.isLarge ? 'width: 20ch;' : ''}
`;

const Label = styled.Text`
  font-family: ${mainTypoBold};
  font-weight: 700;
  font-size: 14px;
`;

const Input: FC<{
  regionCode: string,
  onChangeRegionCode: (value: string) => void,
  phoneNumber: string,
  onChangePhoneNumber: (value: string) => void,
  placeholder?: string,
}> = ({
  regionCode,
  onChangeRegionCode,
  phoneNumber,
  onChangePhoneNumber,
  placeholder,
}) => {
  const phoneNumberInputRef = useRef<TextInput>(null);
  const regionCodeInputRef = useRef<TextInput>(null);
  const {isLargeEnoughForLateralBarWeb} = useIsSmallWeb();
  return(
    <InputBox>
      <Feather
        name='phone'
        size={16}
      />
      <TouchableOpacity onPress={() => regionCodeInputRef?.current?.focus()}>
        <RegionCodeInput
          as={TextInput}
          ref={regionCodeInputRef}
          value={`+${regionCode}`}
          onChangeText={onChangeRegionCode}
          keyboardType='phone-pad'
          autoComplete='tel-country-code'
          isLarge={isLargeEnoughForLateralBarWeb}
        />
      </TouchableOpacity>
      <TouchableOpacity onPress={() => phoneNumberInputRef?.current?.focus()} style={{flexGrow: 1}}>
        <PhoneInput
          as={TextInput}
          ref={phoneNumberInputRef}
          value={phoneNumber}
          onChangeText={onChangePhoneNumber}
          keyboardType='phone-pad'
          autoComplete='tel'
          textContentType='telephoneNumber'
          autoFocus={true}
          placeholder={placeholder}
          isLarge={isLargeEnoughForLateralBarWeb}
        />
      </TouchableOpacity>
    </InputBox>
  );
};

const parse = (text: string) => {
  try {
    return parsePhoneNumber(text);
  } catch (error) {
    return undefined;
  }
};

interface PhoneNumberInputProps {
  value: string;
  onChange: (text: string) => void;
}

const PhoneNumberInput: FC<PhoneNumberInputProps> = ({ value, onChange }) => {
  const { width } = useAvailableDimensions();
  const sessionMargin = BIG_HORIZONTAL_MARGIN_FN(width);
  const parsed = parse(value);
  const { user, language } = useStores();
  const labels = language.translate.MOBILE.ASSISTANCE.INSTANT_HELP;
  const initialRegionCode = user.user?.region ? getPhoneCode(user.user.region as CountryCode) : '32';
  const [regionCode, setRegionCode] = useState<string>(parsed
    ? parsed.countryCallingCode.toString()
    : initialRegionCode);
  const [phoneNumber, setPhoneNumber] = useState<string>(parsed
    ? parsed.nationalNumber.toString()
    : '');
  useEffect(() => {
    console.log(parsed);
    if (parsed) {
      setRegionCode(parsed.countryCallingCode.toString());
      setPhoneNumber(parsed.nationalNumber.toString());
    }
  }, [parsed]);
  const onChangeRegionCode = (regionCodeInput: string) => {
    setRegionCode(regionCodeInput.slice(1));
    if (regionCodeInput.slice(1).length) {
      onChange(`+${regionCodeInput}${phoneNumber}`);
    }
  };
  const onChangePhoneNumber = (phoneNumberInput: string) => {
    setPhoneNumber(phoneNumberInput);
    onChange(`+${regionCode}${phoneNumberInput}`);
  };
  return (
    <Container $sessionMargin={sessionMargin} $windowWidth={width}>
      <Label>
        {labels?.YOUR_PHONE_NUMBER}
      </Label>
      <Input
        regionCode={regionCode}
        onChangeRegionCode={onChangeRegionCode}
        phoneNumber={phoneNumber}
        onChangePhoneNumber={onChangePhoneNumber}
        placeholder={labels?.YOUR_PHONE_NUMBER}
      />
    </Container>
  );
};

export default PhoneNumberInput;